import React, {FC, useEffect, useMemo, useState} from "react";
import {tokenService} from "../../../store/actions";
import {Link, useNavigate, useParams} from "react-router-dom";
import useCommon from "../../../common/hooks/useCommon";
import {formatNumber, formatPercent, getFiat, getTimeLang, serializeQuery, toFormatNumber, toXDCNumber, toXDCNumberWithoutFormat} from '../../../common/utils/function';
import BTable2 from "../../../components/b-table2";
import AddressName from "../address-name";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import CSVConverter from "../../../components/b-CSV-converter";

interface XRC720TokenHolderListProps {
    count: number;
    type?: any;
    topHeadingShow?: boolean;
    pgItemCount?: number;
}

interface TransactionListData {
    address: any;
    block: number;
    timestamp: string | number;
    extraMark: string;
    from: string;
    to: string;
    value: string | number;
    gas_price: string | number;
    token: any;
}

const XRC720TokenHolderList: FC<XRC720TokenHolderListProps> = (props) => {
    const {address, token, holder} = useParams();
    const {getXDC, get0x} = useCommon();

    const {t, i18n} = useTranslation();
    const fiat = useSelector((e: Object) => (e as any)?.common?.fiat) || 'usd';
    const [transactionList, setTransactionList] = useState<TransactionListData[]>(
        []
    );
    const [newTransactionList, setNewTransactionList] = useState<TransactionListData[]>(
        []
    );
    const [transferCount, setTransferCount] = useState({
        token_holders_count: "",
        transfers_count: "",
    });
    const headingShow = props.topHeadingShow ? false : true;
    const [loading, setLoading] = useState(true);
    const initialLimit = props.pgItemCount && typeof props.pgItemCount === 'number' ? props.pgItemCount : 50;
    const [pagination, setPagination] = useState<any>({
        limit: initialLimit,
        page: 1,
    });
    const [totalRecords, setTotalRecords] = useState<any>(0);
    const handle = {
        pagination: (payload: any) => {
            setPagination(payload);
        }
    };
    const navigate = useNavigate();
    const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, url: string) => {
        event.preventDefault();
        navigate(url);
        window.location.reload();
    };

    const getBlocks = async () => {
        try {
            setLoading(true);
            setTransactionList([]);
            tokenService.getTokenHolders(token || address, serializeQuery({...pagination})).then((response) => {
                setTransactionList(response?.items);
                const transformedItems = transformTransactions(response?.items);
                setNewTransactionList(transformedItems)
                setTotalRecords(response?.count)
                setLoading(false);
            }).catch((e) => setLoading(false));
        } catch (err) {
            console.log(err);
        }
    };
    const getTransferCount = async () => {
        try {
            setLoading(true);
            tokenService.getTransferCount(token || holder).then((response) => {
                setTransferCount(response);
                setLoading(false);
            }).catch((e) => setLoading(false));
        } catch (err) {
            console.log(err);
        }
    };
    useEffect(() => {
        getBlocks();
        getTransferCount();
    }, [pagination]);

    const transformTransactions = (transactions: TransactionListData[]): TransactionListData[] => {
        return transactions.map((txn) => {
            const newPercent = formatPercent(toFormatNumber(txn?.value ?? 0, txn?.token?.decimals ?? 0), txn?.token?.total_supply ?? 0) === "0.0000" ? formatPercent((txn?.token?.total_supply && txn?.value) ? Number(txn?.value ?? 0) : 0, txn?.token?.total_supply ? Number(txn?.token?.total_supply) : 1) : formatPercent(toFormatNumber(txn?.value ?? 0, txn?.token?.decimals ?? 0), txn?.token?.total_supply ?? 0)
            const newPercentage = newPercent.toString() + '%';
            const newQuality = toXDCNumber(txn?.value, txn?.token?.decimals, 0)
            const newValue = newQuality.toString();
            const val = getFiat(toXDCNumberWithoutFormat(txn?.value, txn?.token?.decimals) * Number(txn?.token?.exchange_rate ?? 0), 18, fiat, true)
            const value = val.toString();
            return {
                ...txn,
                newPercentage,
                newValue,
                value,
            };
        });
    };

    let fileNameAddress = token || "unknown";
    const fileName = `export-token-${fileNameAddress}.csv`;

    const headerList = ['address.hash', 'newValue', 'newPercentage', 'value'];
    const customHeaderList = {'address.hash': 'Hash', 'newValue': 'Quantity', 'newPercentage': 'Percentage', 'value': 'Value'}

    const header = useMemo(() => {
        return [
            {
                title: `${t("Rank")}`,
                param: "index",
                render: (data: any, index: number) => (pagination.page - 1) * pagination.limit + index + 1,
            },
            {
                title: `${t("Address")}`,
                param: "address",
                headerStyle: {minWidth: "140px"},
                copy: (data: any) => getXDC(data?.address?.hash),
                tooltip: (data: any) => <Link to={`/token/${get0x(address || token)}?address=${get0x(data?.address?.hash)}`}>{getXDC(data?.address?.hash)}</Link>,
                render: (data: any) => (
                    <Link to={`/token/${get0x(address || token)}?address=${get0x(data?.address?.hash)}#`}
                          onClick={(event) => handleLinkClick(event, `/token/${get0x(address || token)}?address=${get0x(data?.address?.hash)}#`)}
                    >
                        <AddressName data={data?.address}/>
                    </Link>
                ),
            },
            {
                title: `${t("Quantity")}`,
                param: "value",
                tooltip: true,
                render: (data: any) => toXDCNumber(data?.value, data?.token?.decimals, 0),
            },
            {
                title: `${t("Percentage")}`,
                param: "value",
                tooltip: true,
                render: (data: any) => (
                    <React.Fragment>
                        {formatPercent(toFormatNumber(data?.value ?? 0, data?.token?.decimals ?? 0), data?.token?.total_supply ?? 0) === "0.0000" ? formatPercent((data?.token?.total_supply && data?.value) ? Number(data?.value ?? 0) : 0, data?.token?.total_supply ? Number(data?.token?.total_supply) : 1) : formatPercent(toFormatNumber(data?.value ?? 0, data?.token?.decimals ?? 0), data?.token?.total_supply ?? 0)}%
                        <div className="progress" style={{height: "4px"}}>
                            <div
                                className="progress-bar"
                                style={{width: `${formatPercent(toFormatNumber(data?.value ?? 0, data?.token?.decimals ?? 0), data?.token?.total_supply ?? 0) === "0.0000" ? formatPercent((data?.token?.total_supply && data?.value) ? Number(data?.value ?? 0) : 0, data?.token?.total_supply ? Number(data?.token?.total_supply) : 1) : formatPercent(toFormatNumber(data?.value ?? 0, data?.token?.decimals ?? 0), data?.token?.total_supply ?? 0)}%`}}
                            />
                        </div>
                    </React.Fragment>
                ),
            },
            {
                title: `${t("Value")}`,
                param: "value",
                headerStyle: {minWidth: "130px"},
                tooltip: true,
                render: (data: any) => (
                    <React.Fragment>
                        {getFiat(toXDCNumberWithoutFormat(data?.value, data?.token?.decimals) * Number(data?.token?.exchange_rate ?? 0), 18, fiat, true)}
                    </React.Fragment>
                ),
            },
        ];
    }, [pagination]);
    return (
        <BTable2
            title={
                <React.Fragment>
                    <div className="d-block d-md-flex justify-content-between align-items-center">
                        <p className="font-b">Top 1,000 holders (From a total of {formatNumber(transferCount?.token_holders_count ?? "")} holders)</p>
                        <div className="my-2">
                            <CSVConverter data={newTransactionList} fileName={fileName} headers={headerList} customHeaders={customHeaderList}/>
                        </div>
                    </div>
                </React.Fragment>}
            topHeading={<React.Fragment>
                <div>
                    <a className="btn btn-sm btn-primary" href={`/token/tokenholderchart/${get0x(address || token)}`}> <i className="bi bi-pie-chart"></i>Token Holding Chart</a>
                </div>
                <span className="text-muted small">(Holders Snapshot taken {getTimeLang(transactionList[0]?.timestamp, t)})</span>
            </React.Fragment>}
            header={header}
            list={transactionList}
            loading={loading}
            pagination={pagination}
            totalRecords={totalRecords}
            onPagination={handle.pagination}
            topHeaderPagination={headingShow}
        />
    );
};

export default XRC720TokenHolderList;
